export const VIAS_LANDING_VARIANT = 'vias-acompanamiento' as const

export type OfferPathId = 'tipi_express' | 'local_rentable' | 'apaga_incendio' | 'asesoria_waitlist'

export type RecommendedPlanId = Exclude<OfferPathId, 'asesoria_waitlist'>

type PaidOfferPath = {
    id: RecommendedPlanId
    label: string
    formLabel: string
    badge?: string
    forYouIf: string
    price: string
    priceNote: string
    includes: string[]
    cta: string
}

export const OFFER_PATHS: PaidOfferPath[] = [
    {
        id: 'tipi_express',
        label: 'Tipi Express',
        formLabel: 'Tipi Express (4 sesiones)',
        forYouIf:
            'Quieres poner Tipi en marcha lo más rápido posible. Te acompañamos dentro de la herramienta, no es consultoría de negocio fuera de Tipi.',
        price: '600 €',
        priceNote: '+ IVA · 4 sesiones de 1 h',
        includes: [
            'Análisis inicial con tus datos en Tipi',
            '4 sesiones para arrancar Tipi al ritmo más rápido',
            'Cierre del mes: cuánto te ha servido Tipi',
            '1 mes de app con 100 albaranes',
        ],
        cta: 'Quiero Tipi Express',
    },
    {
        id: 'local_rentable',
        label: 'Local Rentable',
        formLabel: 'Local Rentable (pack 3 meses)',
        badge: 'Recomendado',
        forYouIf:
            'Quieres convertirte en mejor gestor casi sin tiempo extra: lo haces todo con Tipi, y en 3 meses tendrás el control de cada número de tu negocio y sabrás qué decisiones tomar en cada momento.',
        price: '1.500 €',
        priceNote: '+ IVA · 12 sesiones a tu ritmo · app incluida',
        includes: [
            'Pack de 3 meses con 300 albaranes',
            '12 sesiones de 1 h para usarlas cuando quieras',
            'Formación en gestión para controlar tu negocio',
        ],
        cta: 'Quiero Local Rentable',
    },
    {
        id: 'apaga_incendio',
        label: 'Apaga el incendio',
        formLabel: 'Apaga el incendio (en mi local)',
        forYouIf:
            'Facturas mucho pero pierdes miles sin control. No puedes esperar otra semana: vamos a tu local, volcamos escandallos y te lo dejamos montado para que luego solo subas albaranes.',
        price: '2.000 €',
        priceNote: '+ IVA · viaje y alojamiento aparte',
        includes: [
            'Equipo Tipi en persona en tu local',
            '3 días físicos: volcamos escandallos y te lo dejamos montado',
            'Después solo subes albaranes',
            'Formamos a tu equipo para saber usar Tipi',
            '1 mes de app con 100 albaranes',
        ],
        cta: 'Venid a apagar el incendio',
    },
]

/** Hero line when the visitor arrives from the /empieza quiz with a recommended plan. */
export const RECOMMENDED_PLAN_REASONS: Record<RecommendedPlanId, string> = {
    tipi_express:
        'Por lo que nos has contado, tu punto de partida es Tipi Express: en 4 sesiones dejas Tipi funcionando y empiezas a ver tus números.',
    local_rentable:
        'Por lo que nos has contado, tu punto de partida es Local Rentable: en 3 meses controlas cada número de tu negocio, a tu ritmo.',
    apaga_incendio:
        'Por lo que nos has contado, tu punto de partida es Apaga el incendio: vamos a tu local y en 3 días te lo dejamos montado.',
}

/** Query param the /empieza quiz sets to highlight the recommended plan. */
export const RECOMMENDED_PLAN_PARAM = 'plan'

export const RECOMMENDED_PLAN_BADGE = 'Recomendado para ti'

export function parseRecommendedPlan(value: string | null): RecommendedPlanId | null {
    return OFFER_PATHS.some(path => path.id === value) ? (value as RecommendedPlanId) : null
}

const ASESORIA_WAITLIST_FORM_OPTION = {
    id: 'asesoria_waitlist' as const,
    label: 'Asesoría Tipi (lista de espera)',
}

export const OFFER_PATH_FORM_OPTIONS: { id: OfferPathId; label: string }[] = [
    ...OFFER_PATHS.map(path => ({ id: path.id, label: path.formLabel })),
    ASESORIA_WAITLIST_FORM_OPTION,
]

const OFFER_PATH_LEAD_CONCERN: Record<OfferPathId, string> = {
    tipi_express: 'Plan: Tipi Express',
    local_rentable: 'Plan: Local Rentable',
    apaga_incendio: 'Plan: Apaga el incendio',
    asesoria_waitlist: 'Plan: Asesoría Tipi (lista de espera)',
}

export function getOfferPathLeadConcern(pathId: OfferPathId): string {
    return OFFER_PATH_LEAD_CONCERN[pathId]
}

/** Premium anchor shown on this page. CTA soft-gates to the form waitlist until `/asesoria` ships. */
export const ASESORIA_ANCHOR = {
    eyebrow: 'Solo 1 cliente al mes',
    label: 'Asesoría Tipi · Hostelería rentable',
    forYouIf:
        'Si tu pasión es el oficio, no la gestión: nos quedamos con los números para que tú te centres en cocina, sala, producto y cliente.',
    capacityNote: 'Priorizamos un servicio excelente. Si este mes ya está cogido, te avisamos para el siguiente.',
    faces: [
        {
            src: '/asesoria/hero.jpg',
            alt: 'Samuel Carrillo',
            name: 'Samuel',
        },
        {
            src: '/asesoria/javi.jpg',
            alt: 'Javier Porras',
            name: 'Javi',
        },
    ],
    price: '14.000 €',
    priceVariable: '+ 10 %',
    priceVariableLabel: 'de la mejora de beneficio que generemos juntos',
    priceNote: 'Plan anual · + IVA · app incluida · hasta 2 locales',
    includes: [
        'Facturas y ventas preparadas para tu gestoría el día 1 de cada mes',
        'Reclamamos a tus proveedores las facturas que falten',
        'Negociamos con cualquiera de tus proveedores, con precios especiales',
        'App Tipi, red de contactos del sector y búsqueda de financiación para nuevos proyectos',
        'Visitas presenciales trimestrales (alojamiento y transporte aparte)',
    ],
    cta: 'Entrar en lista de espera',
    offerPathId: 'asesoria_waitlist' as const,
} as const

export const TOOL_ONLY_EXIT = {
    lead: '¿No necesitas acompañamiento?',
    body: 'Contrata ya la herramienta y ponle solución por ti mismo.',
    cta: 'Ir a Tipi herramienta',
} as const
